import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faIcons, faUpload, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FkMdeIconDialogComponent, IconSelection } from '../fk-mde/fk-mde-icon-dialog/fk-mde-icon-dialog.component';
import { AuthService } from '../../../subscription/services/auth/auth.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'formly-field-avatar',
  standalone: true,
  imports: [CommonModule, FormlyModule, FontAwesomeModule],
  template: `
    <div class="avatar-field" [class.text-center]="!props['leftAlign']">
      <!-- Label with Action Buttons (right-aligned) -->
      <div class="d-flex align-items-center justify-content-between mb-1">
        <label class="form-label mb-0">{{ props.label || 'Avatar' }}</label>
        <div class="d-flex align-items-center gap-2">
          <button
            type="button"
            class="btn btn-sm btn-link p-0"
            (click)="openIconPicker()"
            [disabled]="isUploading"
            title="Choose icon"
          >
            <fa-icon [icon]="icons.icon"></fa-icon>
          </button>
          <label class="btn btn-sm btn-link p-0 mb-0" [class.disabled]="isUploading" title="Upload image">
            <fa-icon [icon]="icons.upload"></fa-icon>
            <input
              type="file"
              accept="image/*"
              class="d-none"
              (change)="onFileSelected($event)"
              [disabled]="isUploading"
            />
          </label>
          @if (formControl.value) {
            <button
              type="button"
              class="btn btn-sm btn-link p-0 text-danger"
              (click)="removeAvatar()"
              [disabled]="isUploading"
              title="Remove avatar"
            >
              <fa-icon [icon]="icons.trash"></fa-icon>
            </button>
          }
          @if (isUploading) {
            <span class="text-muted small">
              <span class="spinner-border spinner-border-sm me-1"></span>
            </span>
          }
        </div>
      </div>

      <!-- Avatar Preview -->
      <div class="avatar-preview-container d-flex" [class.justify-content-center]="!props['leftAlign']">
        @if (props['logo']) {
          <!-- Logo mode: a full-width well that lets the image render at
               its natural size inside. -->
          <div class="avatar-logo-well">
            @if (displayUrl) {
              <img [src]="displayUrl" alt="Logo" class="avatar-logo" />
            } @else {
              <span class="text-muted small">No logo</span>
            }
          </div>
        } @else if (displayUrl) {
          <img
            [src]="displayUrl"
            alt="Avatar"
            class="avatar-preview"
            [class.avatar-square]="props['square']"
          />
        } @else {
          <div class="avatar-placeholder" [class.avatar-square]="props['square']">
            <span class="text-muted small">No avatar</span>
          </div>
        }
      </div>

      @if (error) {
        <div class="text-danger small mt-2">{{ error }}</div>
      }

      @if (props.description) {
        <small class="form-text text-muted mt-2 d-block">{{ props.description }}</small>
      }
    </div>
  `,
  styles: [`
    .avatar-preview-container {
      flex-shrink: 0;
    }

    .avatar-preview {
      width: 130px;
      height: 130px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid var(--bs-border-color);
      display: block;
    }

    .avatar-placeholder {
      width: 130px;
      height: 130px;
      border-radius: 50%;
      border: 2px dashed var(--bs-border-color);
      background: var(--bs-light);
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 0.5rem;
    }

    .avatar-square {
      border-radius: 0.5rem;
      width: 100%;
      height: auto;
      aspect-ratio: 1;
    }

    // Logo mode: full-width well container with the border, hosts an
    // image rendered at its natural size (capped by the well's height).
    .avatar-logo-well {
      width: 100%;
      min-height: 90px;
      max-height: 90px;
      border-radius: 0.25rem;
      border: 1px solid var(--bs-border-color);
      background-color: var(--bs-body-bg);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.5rem;
      overflow: hidden;
    }
    .avatar-logo {
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }

    label.disabled {
      pointer-events: none;
      opacity: 0.65;
    }
  `],
})
export class FormlyAvatarComponent extends FieldType<FieldTypeConfig> {
  private modalService = inject(NgbModal);
  private authService = inject(AuthService);

  icons = {
    icon: faIcons,
    upload: faUpload,
    trash: faTrash,
  };

  isUploading = false;
  error?: string;

  /** Transform /jaxfs/ URLs to full URLs for display */
  get displayUrl(): string | null {
    const url = this.formControl.value;
    if (!url) return null;
    return url.replace('/jaxfs/', environment.JAXFS_ROOT);
  }

  async openIconPicker(): Promise<void> {
    const modalRef = this.modalService.open(FkMdeIconDialogComponent, {
      size: 'lg',
      centered: false,
    });

    // Avatar mode: use fixed size, hide size selector
    modalRef.componentInstance.avatarMode = true;

    try {
      const result: IconSelection = await modalRef.result;
      if (result?.url) {
        this.formControl.setValue(result.url);
        this.formControl.markAsDirty();
        this.error = undefined;
      }
    } catch {
      // Modal was dismissed
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.error = 'Please select an image file';
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.error = 'Image must be less than 5MB';
      return;
    }

    this.isUploading = true;
    this.error = undefined;

    try {
      const url = await this.authService.uploadProfileAsset(file, 'avatar');
      this.formControl.setValue(url);
      this.formControl.markAsDirty();
    } catch (err: unknown) {
      console.error('Avatar upload error:', err);
      this.error = err instanceof Error ? err.message : 'Failed to upload image';
    } finally {
      this.isUploading = false;
      // Reset the input so the same file can be selected again
      input.value = '';
    }
  }

  removeAvatar(): void {
    this.formControl.setValue(null);
    this.formControl.markAsDirty();
    this.error = undefined;
  }
}
